import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, LogIn, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_KEY = 'marketplace_vendor_token';

const MarketplaceVendorLoginPage = () => {
  const navigate = useNavigate();
  // When the URL is `/marketplace-vendor/login/:companyId` we load
  // tenant branding (logo + name + primary color) and scope login to
  // that tenant. The bare `/marketplace-vendor/login` keeps working
  // as a generic fallback.
  const { companyId } = useParams();
  const [branding, setBranding] = useState(null);
  const [brandingError, setBrandingError] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const requestReset = async () => {
    if (!forgotUsername.trim()) {
      toast.error('Username required');
      return;
    }
    try {
      await axios.post(`${API}/marketplace-vendor/forgot-password`, {
        username: forgotUsername.trim().toLowerCase(),
        company_id: companyId || undefined,
      });
      setForgotSent(true);
    } catch (e) {
      // Always show success to avoid username enumeration
      setForgotSent(true);
    }
  };

  useEffect(() => {
    if (!companyId) return;
    axios
      .get(`${API}/marketplace/onboarding/${companyId}/page-settings`)
      .then((r) => setBranding(r.data))
      .catch(() => setBrandingError(true));
  }, [companyId]);

  const submit = async () => {
    if (!username || !password) {
      toast.error('Username and password required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/marketplace-vendor/login`, {
        username,
        password,
        company_id: companyId || undefined,
      });
      localStorage.setItem(TOKEN_KEY, r.data.access_token);
      localStorage.setItem('marketplace_vendor_profile', JSON.stringify(r.data.vendor));
      toast.success(`Welcome, ${r.data.vendor.vendor_name}`);
      navigate('/marketplace-vendor/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const primary = branding?.primary_color || '#D4AF37';
  const tenantName = branding?.company_name;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={tenantName} className="w-12 h-12 rounded-lg object-cover bg-slate-800" />
          ) : (
            <Building2 className="w-8 h-8" style={{ color: primary }} />
          )}
          <div>
            <h1 className="text-2xl font-semibold" data-testid="mv-login-title">
              {tenantName ? `${tenantName} Vendor Sign-in` : 'Marketplace Vendor Sign-in'}
            </h1>
            <p className="text-xs text-slate-500">Access your PO queue, labels, and payouts.</p>
          </div>
        </div>

        {brandingError && companyId && (
          <div className="mb-4 p-3 rounded bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300" data-testid="mv-login-branding-error">
            We couldn&apos;t find a marketplace for that company. Double-check the link your admin shared, or use the generic <a href="/marketplace-vendor/login" className="underline">sign-in page</a>.
          </div>
        )}

        <div className="space-y-3">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase().trim())}
            placeholder="Username"
            className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 focus:outline-none text-sm"
            style={{ borderColor: undefined }}
            data-testid="mv-login-username"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 focus:outline-none text-sm"
            data-testid="mv-login-password"
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full px-4 py-2.5 rounded-lg font-semibold text-slate-950 flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: primary }}
            data-testid="mv-login-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            Sign In
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={() => { setForgotUsername(username); setForgotSent(false); setForgotOpen(true); }}
              className="text-xs text-slate-500 hover:text-slate-300 hover:underline"
              data-testid="mv-login-forgot"
            >
              Forgot password?
            </button>
          </div>
        </div>
      </div>

      {/* Forgot-password modal */}
      {forgotOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setForgotOpen(false)}>
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="mv-forgot-modal"
          >
            <h2 className="text-lg font-semibold mb-2 text-slate-100">Forgot password?</h2>
            {forgotSent ? (
              <>
                <p className="text-sm text-slate-400 mb-4">
                  If an account with that username exists, your admin has been notified to reset it.
                  They&apos;ll share the new password with you out-of-band.
                </p>
                <button
                  onClick={() => setForgotOpen(false)}
                  className="w-full px-4 py-2 rounded font-semibold text-slate-950 text-sm"
                  style={{ background: primary }}
                  data-testid="mv-forgot-close"
                >
                  Close
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-400 mb-4">
                  Enter your username and we&apos;ll notify the admin to reset your password.
                </p>
                <input
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  placeholder="Username"
                  className="w-full px-3 py-2 rounded bg-slate-950 border border-slate-700 text-sm mb-4"
                  data-testid="mv-forgot-username"
                />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setForgotOpen(false)} className="px-3 py-2 text-xs text-slate-500 hover:text-slate-200">Cancel</button>
                  <button
                    onClick={requestReset}
                    className="px-4 py-2 rounded font-semibold text-slate-950 text-sm"
                    style={{ background: primary }}
                    data-testid="mv-forgot-submit"
                  >
                    Send request
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceVendorLoginPage;
